<template>
  <div class="bg-gray-100">
    <div class="flex items-center gap-6 bg-white ml-8" style="font-family: Roboto;" :class="tabWrapperClass">
      <div v-for="(tab, index) in tabs" :key="getTabKey(tab, index)" class="group">
        <div class="ag-transition" :class="tabClasses(tab)" :style="tabStyle(tab)" @click="selectTab(tab)">
          <div class="whitespace-nowrap">
            {{ tab.label }}
          </div>
        </div>
      </div>
    </div>

    <div :class="componentWrapperClass">
      <template v-if="activeTabComponent">
        <keep-alive>
          <component :is="activeTabComponent" v-bind="componentProps" />
        </keep-alive>
      </template>
      <template v-else>
        <slot name="tab" :tab="activeTabObject"></slot>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: "Tabs",
  props: {
    tabs: {
      type: Array,
      default: () => []
    },
    value: {
      type: [String, Number, Object],
      default: null
    },
    tabClass: {
      type: String,
      default: "border-b-2"
    },
    tabWrapperClass: {
      type: String,
      default: ""
    },
    componentWrapperClass: {
      type: String,
      default: "mt-4 mx-8"
    },
    componentProps: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      internalActiveTab: this.getInitialTab()
    };
  },
  computed: {
    activeTab() {
      return this.value != null ? this.value : this.internalActiveTab;
    },
    activeTabObject() {
      return this.tabs.find(tab => this.getTabValue(tab) === this.activeTab) || null;
    },
    activeTabComponent() {
      if (!this.activeTabObject) {
        return null;
      }
      return (
        this.activeTabObject.component ||
        this.activeTabObject.Component ||
        null
      );
    }
  },
  watch: {
    value(newVal) {
      if (newVal != null) {
        this.internalActiveTab = newVal;
      }
    },
    activeTab(newTab) {
      this.$emit("input", newTab);
      this.$emit("change", newTab);
    }
  },
  methods: {
    getInitialTab() {
      if (this.value != null) {
        return this.value;
      }
      return this.tabs.length ? this.getTabValue(this.tabs[0]) : null;
    },
    getTabValue(tab) {
      return tab.value != null ? tab.value : tab.label;
    },
    getTabKey(tab, index) {
      return tab.value || tab.label || index;
    },
    isActive(tab) {
      return this.getTabValue(tab) === this.activeTab;
    },
    selectTab(tab) {
      const value = this.getTabValue(tab);
      if (value !== this.activeTab) {
        this.internalActiveTab = value;
        this.$emit("input", value);
        this.$emit("change", value);
      }
    },
    tabClasses(tab) {
      const base = [
        "flex items-center justify-center gap-2 px-1 pb-5 pt-0",
        "font-medium uppercase leading-none text-14",
        this.tabClass
      ];
      if (this.isActive(tab)) {
        base.push(
          "border-b-4 border-blue-600 text-blue-700"
        );
      } else {
        base.push(
          "border-b-4 border-transparent text-gray-700 hover:text-gray-900"
        );
      }
      base.push("cursor-pointer");
      return base.join(" ");
    },
    tabStyle() {
      return {
        letterSpacing: "1.4px"
      };
    }
  }
};
</script>

<style scoped>
.ag-transition {
  transition: all 0.3s ease;
}
</style>
